import React, { useEffect, useRef, useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  useTheme,
  alpha,
  Chip
} from '@mui/material';
import NotesIcon from '@mui/icons-material/Notes';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WeatherSearch from './WeatherSearch';
import useNotesAutoFill from './hooks/useNotesAutoFill';
import GooglePlacesAutocomplete from './GooglePlacesAutocomplete';
import LocationMap from './LocationMap';
import weatherService from '../../services/weatherService';

/**
 * Notes Component - Handles general notes, parking notes, and safety notes
 * @param {Object} data - The callsheet data
 * @param {Function} onUpdate - Function to update the callsheet data
 * @param {Object} styles - Theme-aware styles
 * @returns {JSX.Element} - Rendered component
 */
const Notes = ({ data, onUpdate, styles, projectId }) => {
  const theme = useTheme();
  const [weatherSearchOpen, setWeatherSearchOpen] = useState(false);
  const { suggestions, regenerate, loading: regenLoading, lastGeneratedAt } = useNotesAutoFill({ projectId, shootingDay: data.shootingDay });
  const [mapOpen, setMapOpen] = useState(false);
  const [activeParkingIndex, setActiveParkingIndex] = useState(null);
  const [safetySeverity, setSafetySeverity] = useState('');
  const [weatherCopied, setWeatherCopied] = useState(false);

  // Persist collapsible state per project/day
  const accordionKey = React.useMemo(() => (
    projectId ? `notes_accordions_${projectId}_${data?.shootingDay || 1}` : null
  ), [projectId, data?.shootingDay]);
  const [expandedPanels, setExpandedPanels] = useState({
    parking: false,
    safety: false,
    weather: false,
    camera: false,
    lighting: false,
    props: false,
    sound: false,
    vfx: false,
    specialFx: false,
    vehicles: false,
    stunts: false,
    animals: false,
    hairMakeup: false,
    misc: false
  });
  useEffect(() => {
    if (!accordionKey) return;
    try {
      const raw = localStorage.getItem(accordionKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          setExpandedPanels(prev => ({ ...prev, ...parsed }));
        }
      }
    } catch {}
  }, [accordionKey]);
  useEffect(() => {
    if (!accordionKey) return;
    try {
      localStorage.setItem(accordionKey, JSON.stringify(expandedPanels));
    } catch {}
  }, [accordionKey, expandedPanels]);
  const togglePanel = (key) => (_e, isExpanded) => {
    setExpandedPanels(prev => ({ ...prev, [key]: isExpanded }));
  };

  // Expand/Collapse all accordions
  const setAllPanels = (value) => {
    setExpandedPanels(prev => Object.keys(prev).reduce((acc, k) => ({ ...acc, [k]: value }), {}));
  };
  const handleExpandAll = () => setAllPanels(true);
  const handleCollapseAll = () => setAllPanels(false);

  // Keyboard shortcuts: Shift+E expand all, Shift+C collapse all
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!e.shiftKey) return;
      const key = e.key?.toLowerCase();
      if (key === 'e') {
        e.preventDefault();
        setAllPanels(true);
      } else if (key === 'c') {
        e.preventDefault();
        setAllPanels(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    if (field === 'weatherNotes') {
      // Live-parse sunrise/sunset as user types/pastes
      const { sunrise, sunset } = parseSunTimesFromText(value);
      const sunriseISO = sunrise ? timeToDate(sunrise, data?.date) : undefined;
      const sunsetISO  = sunset  ? timeToDate(sunset,  data?.date) : undefined;
      onUpdate({
        weatherNotes: value,
        generalInfo: {
          ...(data?.generalInfo || {}),
          ...(sunriseISO ? { sunriseTime: sunriseISO } : {}),
          ...(sunsetISO  ? { sunsetTime:  sunsetISO  } : {}),
        }
      });
    } else {
      onUpdate({ [field]: value });
    }
  };

  // Safety helpers
  const safetyLen = (data?.safetyNotes || '').length;
  const commonSafety = [
    'Heat advisory in effect. Hydrate hourly.',
    'PPE required in Shop A.',
    'Slippery floors. Use caution walking between sets.',
    'Strobe effects in use. Notify light-sensitive crew.',
    'High-voltage equipment in operation. Authorized personnel only.',
    'Fire lanes must remain clear at all times.',
    'Animal on set. Follow handler instructions.',
    'Stunt sequence scheduled. Only essential crew on set.'
  ];

  const setSeverity = (level) => {
    setSafetySeverity(level);
    const text = data?.safetyNotes || '';
    const prefixRe = /^\s*\[(LOW|MED|HIGH)\]\s*/i;
    const newPrefix = level ? `[${level}] ` : '';
    const withoutOld = text.replace(prefixRe, '');
    onUpdate({ safetyNotes: `${newPrefix}${withoutOld}`.trim() });
  };

  // Parking locations helpers
  const parkingLocations = Array.isArray(data.parkingLocations) ? data.parkingLocations : [];

  const parkingStorageKey = React.useMemo(() => {
    const day = data?.shootingDay || 1;
    return projectId ? `parkingLocations_${projectId}_${day}` : null;
  }, [projectId, data?.shootingDay]);

  const updateParkingLocations = (next) => {
    onUpdate({ parkingLocations: next });
    // Persist to localStorage so refresh keeps data even if server doesn't store it yet
    try {
      if (parkingStorageKey) {
        // sanitize to a lean payload
        const lean = (Array.isArray(next) ? next : []).map(p => ({
          type: p?.type || 'other',
          name: p?.name || '',
          address: p?.address || '',
          phone: p?.phone || '',
          notes: p?.notes || '',
          placeData: p?.placeData ? {
            placeId: p.placeData.placeId || '',
            address: p.placeData.address || p.address || '',
            coordinates: p.placeData.coordinates ? {
              lat: p.placeData.coordinates.lat,
              lng: p.placeData.coordinates.lng
            } : undefined
          } : undefined
        }));
        localStorage.setItem(parkingStorageKey, JSON.stringify(lean));
      }
    } catch (e) {
      // non-fatal
      // eslint-disable-next-line no-console
      console.warn('Could not persist parkingLocations to localStorage', e);
    }
  };

  const addParkingLocation = () => {
    const next = [...parkingLocations, { type: 'other', name: '', address: '', phone: '', notes: '', placeData: null }];
    updateParkingLocations(next);
  };

  // Load persisted parking locations on mount and when day changes
  useEffect(() => {
    if (!parkingStorageKey) return;
    try {
      const raw = localStorage.getItem(parkingStorageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      // Only overwrite state if localStorage actually has items
      if (Array.isArray(parsed) && parsed.length > 0) {
        onUpdate({ parkingLocations: parsed });
      } else {
        // Seed localStorage from current (in-memory) state if available
        if (Array.isArray(parkingLocations) && parkingLocations.length > 0) {
          const lean = parkingLocations.map(p => ({
            type: p?.type || 'other',
            name: p?.name || '',
            address: p?.address || '',
            phone: p?.phone || '',
            notes: p?.notes || '',
            placeData: p?.placeData ? {
              placeId: p.placeData.placeId || '',
              address: p.placeData.address || p.address || '',
              coordinates: p.placeData.coordinates ? {
                lat: p.placeData.coordinates.lat,
                lng: p.placeData.coordinates.lng
              } : undefined
            } : undefined
          }));
          localStorage.setItem(parkingStorageKey, JSON.stringify(lean));
        } else {
          // Neither localStorage nor memory has items: keep state as-is (don't force clear)
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Could not load parkingLocations from localStorage', e);
      // Do not overwrite state on error
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parkingStorageKey]);

  const removeParkingLocation = (index) => {
    const next = parkingLocations.filter((_, i) => i !== index);
    updateParkingLocations(next);
  };

  const changeParkingField = (index, field, value) => {
    const next = parkingLocations.map((p, i) => i === index ? { ...p, [field]: value } : p);
    updateParkingLocations(next);
  };

  const handlePlaceSelect = (index, place) => {
    const next = parkingLocations.map((p, i) => i === index ? {
      ...p,
      name: place?.name || p.name,
      address: place?.address || p.address,
      placeData: place || p.placeData
    } : p);
    updateParkingLocations(next);
  };

  // Extract Sunrise/Sunset (e.g., "Sunrise: 6:31 AM", "Sunset 7:45 PM") and map to Date using the callsheet date
  const parseSunTimesFromText = (text) => {
    if (!text) return { sunrise: null, sunset: null };
    try {
      const src = String(text).replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
      const mSunrise = src.match(/sunrise[^0-9a-zA-Z]*([0-1]?\d:[0-5]\d(?::[0-5]\d)?\s*[AP]M)/i);
      const mSunset  = src.match(/sunset[^0-9a-zA-Z]*([0-1]?\d:[0-5]\d(?::[0-5]\d)?\s*[AP]M)/i);
      return {
        sunrise: mSunrise && mSunrise[1] ? mSunrise[1].toUpperCase() : null,
        sunset:  mSunset  && mSunset[1]  ? mSunset[1].toUpperCase()  : null,
      };
    } catch {
      return { sunrise: null, sunset: null };
    }
  };
  const timeToDate = (timeStr, baseDate) => {
    if (!timeStr || !baseDate) return null;
    try {
      const d = new Date(baseDate);
      const m = timeStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AP]M)$/i);
      if (!m) return null;
      let hh = parseInt(m[1], 10);
      const mm = parseInt(m[2], 10);
      const ss = m[3] ? parseInt(m[3], 10) : 0;
      const ampm = (m[4] || m[3]).toUpperCase();
      if (ampm === 'PM' && hh < 12) hh += 12;
      if (ampm === 'AM' && hh === 12) hh = 0;
      d.setHours(hh, mm, ss, 0);
      return d.toISOString();
    } catch {
      return null;
    }
  };

  // If Weather Notes contain a date/location but not Sunrise/Sunset, fetch sunrise/sunset by location and set generalInfo
  useEffect(() => {
    const notes = data?.weatherNotes || '';
    const hasSunTimes = /sunrise|sunset/i.test(notes);
    const baseDate = data?.date;
    if (!baseDate) return;
    if (hasSunTimes) return; // already provided explicitly
    // Try to parse a location from patterns like: "<date> - <location>: ..."
    const m = notes.match(/-\s*([^:]+):/);
    const location = (m && m[1]) ? m[1].trim() : (data?.mainLocation || '');
    if (!location) return;
    const key = `${location}|${new Date(baseDate).toDateString()}`;
    if (lastSunKeyRef.current === key) return;
    let cancelled = false;
    (async () => {
      try {
        const w = await weatherService.getCurrentWeather(location);
        // Normalize to AM/PM with optional seconds then to ISO on the callsheet date
        const sunriseISO = w?.sunrise ? timeToDate(String(w.sunrise).toUpperCase().replace(/\s+/g, ' ').trim(), baseDate) : undefined;
        const sunsetISO  = w?.sunset  ? timeToDate(String(w.sunset).toUpperCase().replace(/\s+/g, ' ').trim(),  baseDate) : undefined;
        if (!cancelled && (sunriseISO || sunsetISO)) {
          onUpdate({
            generalInfo: {
              ...(data?.generalInfo || {}),
              ...(sunriseISO ? { sunriseTime: sunriseISO } : {}),
              ...(sunsetISO  ? { sunsetTime:  sunsetISO  } : {}),
            }
          });
          lastSunKeyRef.current = key;
        }
      } catch (e) {
        // swallow; non-blocking enhancement
      }
    })();
    return () => { cancelled = true; };
  }, [data?.weatherNotes, data?.date, data?.mainLocation, onUpdate, data?.generalInfo]);
  const handleWeatherSelect = (weatherNotes) => {
    const { sunrise, sunset } = parseSunTimesFromText(weatherNotes);
    const sunriseISO = sunrise ? timeToDate(sunrise, data?.date) : undefined;
    const sunsetISO  = sunset  ? timeToDate(sunset,  data?.date) : undefined;
    onUpdate({ 
      weatherNotes,
      generalInfo: {
        ...(data?.generalInfo || {}),
        sunriseTime: sunriseISO ?? (data?.generalInfo?.sunriseTime || undefined),
        sunsetTime:  sunsetISO  ?? (data?.generalInfo?.sunsetTime  || undefined),
      }
    });
    setWeatherSearchOpen(false);
  };

  // Helpers: copy and clear field utilities
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text || '');
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleCopyWeather = async () => {
    const ok = await copyToClipboard(data?.weatherNotes || '');
    setWeatherCopied(ok);
    setTimeout(() => setWeatherCopied(false), 1500);
  };

  const handleClearWeather = () => {
    onUpdate({ weatherNotes: '' });
  };

  const appendToField = (field, value) => {
    const current = data?.[field] || '';
    const trimmed = (value || '').trim();
    if (!trimmed) return;
    const separator = current && !current.endsWith('\n') ? '\n' : '';
    onUpdate({ [field]: `${current}${separator}${trimmed}` });
  };

  const renderSuggestionChips = (label, fieldKey, items = []) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
      <Box sx={{ mt: 0.5, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>{label} suggestions:</Typography>
        {items.slice(0, 10).map((txt, idx) => (
          <Chip
            key={`${fieldKey}_sugg_${idx}`}
            label={txt}
            size="small"
            variant="outlined"
            onClick={() => appendToField(fieldKey, txt)}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Box>
    );
  };

  // Ref for sun times auto-fetch deduplication
  const lastSunKeyRef = useRef('');

  // Auto-apply: ALWAYS REPLACE notes with current day's suggestions (no appending)
  const prevSignatureRef = useRef('');
  useEffect(() => {
    if (!projectId) return;
    if (!suggestions) return;
    
    const currentDay = data?.shootingDay || 1;
    // Create a signature to avoid repeated updates on identical suggestion sets
    const sig = JSON.stringify({ day: currentDay, suggestions });
    if (prevSignatureRef.current === sig) return;

    const map = [
      { key: 'camera', field: 'cameraNotes' },
      { key: 'lighting', field: 'lightingNotes' },
      { key: 'props', field: 'propsNotes' },
      { key: 'sound', field: 'soundNotes' },
      { key: 'vfx', field: 'vfxNotes' },
      { key: 'specialFx', field: 'specialFxNotes' },
      { key: 'vehicles', field: 'vehiclesNotes' },
      { key: 'stunts', field: 'stuntsNotes' },
      { key: 'animals', field: 'animalsNotes' },
      { key: 'hairMakeup', field: 'hairMakeupNotes' },
      { key: 'misc', field: 'miscNotes' }
    ];

    // ALWAYS replace notes with current day's suggestions (no appending)
    const updates = {};
    let hasChange = false;

    map.forEach(({ key, field }) => {
      const items = (suggestions?.[key] || []).map(s => (s || '').trim()).filter(Boolean);
      const newValue = items.join(', ');
      // Only update if value is different
      if (newValue !== (data?.[field] || '')) {
        updates[field] = newValue;
        hasChange = true;
      }
    });

    if (hasChange) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('Notes auto-fill: Replacing notes for day', currentDay, Object.keys(updates));
      }
      onUpdate(updates);
    }
    prevSignatureRef.current = sig;
  }, [projectId, data?.shootingDay, suggestions, onUpdate, data]);

  // Shared styles for clean, consistent sections
  const sectionCard = {
    p: 2.5,
    borderRadius: 1,
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.black, 0.2)
      : alpha(theme.palette.grey[50], 0.3)
  };
  const titleSx = {
    mb: 1.5,
    fontWeight: 700,
    letterSpacing: '.02em',
    textTransform: 'uppercase',
    color: theme.palette.text.primary
  };
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      fontSize: '0.95rem',
      fontWeight: 600
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.background.default, 0.3)
        : alpha(theme.palette.grey[50], 0.5),
      borderRadius: 1,
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderWidth: 1.5
      },
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.default, 0.4)
          : alpha(theme.palette.grey[50], 0.8),
        '& fieldset': {
          borderColor: theme.palette.primary.main
        }
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.05)
          : alpha(theme.palette.primary.main, 0.02),
        '& fieldset': {
          borderWidth: 2
        }
      }
    }
  };
  const accordionSx = {
    borderRadius: 1,
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    overflow: 'hidden',
    background: 'transparent',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 4px 16px rgba(0,0,0,0.3)'
      : '0 2px 8px rgba(0,0,0,0.06)',
    '&:before': {
      display: 'none'
    }
  };

  return (
    <Paper 
      elevation={0}
      sx={{ 
        ...(styles?.primarySection || {}),
        p: 4, 
        mb: 4,
        borderRadius: 1,
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        backgroundColor: theme.palette.background.paper
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 4,
        pb: 3,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.15)
                : alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <NotesIcon sx={{ color: 'primary.main', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography 
              variant="h4" 
              component="h2"
              sx={{ 
                fontWeight: 700,
                letterSpacing: '-0.5px',
                fontSize: '1.75rem',
                color: theme.palette.text.primary,
                mb: 0.5
              }}
            >
              Notes
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontSize: '0.875rem'
              }}
            >
              Centralized production notes and department callouts
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip 
            label={`${Object.keys(data).filter(k => data[k]).length} Sections`} 
            size="small" 
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              fontWeight: 600,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}
          />
          <Chip 
            label={`Day ${data?.shootingDay || 1}`} 
            size="small" 
            sx={{
              bgcolor: alpha(theme.palette.secondary.main, 0.1),
              color: theme.palette.secondary.main,
              fontWeight: 600,
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`
            }}
          />
          <Tooltip title="Expand all sections">
            <Button 
              size="small" 
              variant="outlined" 
              onClick={handleExpandAll}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 600,
                borderWidth: 1.5,
                '&:hover': {
                  borderWidth: 1.5
                }
              }}
            >
              Expand All
            </Button>
          </Tooltip>
          <Tooltip title="Collapse all sections">
            <Button 
              size="small" 
              variant="outlined" 
              onClick={handleCollapseAll}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 600,
                borderWidth: 1.5,
                '&:hover': {
                  borderWidth: 1.5
                }
              }}
            >
              Collapse All
            </Button>
          </Tooltip>
        </Box>
      </Box>
      
      <Grid container spacing={3}>
        
        
        

        {/* Parking Locations with Google Maps */}
        <Grid item xs={12}>
          <Accordion 
            elevation={0} 
            expanded={expandedPanels.parking} 
            onChange={togglePanel('parking')} 
            sx={accordionSx}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box>
                <Typography variant="subtitle1" component="h3" sx={titleSx}>Parking Locations</Typography>
                <Typography variant="body2" sx={{ mt: -1, opacity: 0.7 }}>Add one or more parking locations. Pick an address, add a label and notes, and verify on the map.</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
            <Box sx={sectionCard}>

            {(parkingLocations.length === 0) && (
              <Box sx={{ mb: 2, opacity: 0.7 }}>No parking locations added.</Box>
            )}

            {parkingLocations.map((loc, index) => (
              <Box key={index} sx={{ p: 2, mb: 2, border: `1px dashed ${alpha(theme.palette.divider, 0.8)}`, borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Label (e.g., Crew Lot, Overflow)"
                      value={loc.name || ''}
                      onChange={(e) => changeParkingField(index, 'name', e.target.value)}
                      sx={textFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Select
                      fullWidth
                      value={loc.type || 'other'}
                      label="Type"
                      onChange={(e) => changeParkingField(index, 'type', e.target.value)}
                      sx={{
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.background.default, 0.3)
                          : alpha(theme.palette.grey[50], 0.5),
                        borderRadius: 1,
                        transition: 'all 0.2s ease',
                        '& fieldset': {
                          borderWidth: 1.5,
                          borderRadius: 1
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main
                        },
                        '&.Mui-focused fieldset': {
                          borderWidth: 2
                        }
                      }}
                    >
                      <MenuItem value="crew">Crew Parking</MenuItem>
                      <MenuItem value="basecamp">Basecamp</MenuItem>
                      <MenuItem value="productionOffice">Production Office</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={loc.phone || ''}
                      onChange={(e) => changeParkingField(index, 'phone', e.target.value)}
                      sx={textFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <GooglePlacesAutocomplete
                      label="Parking Address"
                      value={loc.address || ''}
                      onChange={(val) => changeParkingField(index, 'address', val)}
                      onPlaceSelect={(place) => handlePlaceSelect(index, place)}
                      sx={textFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label="Notes"
                      value={loc.notes || ''}
                      onChange={(e) => changeParkingField(index, 'notes', e.target.value)}
                      sx={textFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={() => { setActiveParkingIndex(index); setMapOpen(true); }}
                    >
                      View Map
                    </Button>
                    <Button color="error" variant="text" onClick={() => removeParkingLocation(index)}>Remove</Button>
                  </Grid>
                </Grid>
              </Box>
            ))}

            <Button variant="contained" onClick={addParkingLocation}>
              Add Parking Location
            </Button>
            </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>
        
        <Grid item xs={12} md={12}>
          <Accordion elevation={0} expanded={expandedPanels.safety} onChange={togglePanel('safety')} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" component="h3" sx={titleSx}>Safety Notes</Typography>
            </AccordionSummary>
            <AccordionDetails>
            <Box sx={sectionCard}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>Severity:</Typography>
              <Button size="small" variant={/^\[LOW\]/i.test(data?.safetyNotes || '') ? 'contained' : 'outlined'} onClick={() => setSeverity('LOW')}>LOW</Button>
              <Button size="small" variant={/^\[MED\]/i.test(data?.safetyNotes || '') ? 'contained' : 'outlined'} onClick={() => setSeverity('MED')}>MED</Button>
              <Button size="small" color="error" variant={/^\[HIGH\]/i.test(data?.safetyNotes || '') ? 'contained' : 'outlined'} onClick={() => setSeverity('HIGH')}>HIGH</Button>
              <Button
                size="small"
                variant={!/^\[(LOW|MED|HIGH)\]/i.test(data?.safetyNotes || '') ? 'contained' : 'outlined'}
                onClick={() => { setSafetySeverity(''); onUpdate({ safetyNotes: '' }); }}
              >
                Clear
              </Button>
            </Box>
            <TextField
              fullWidth
              label="Safety Notes"
              value={data.safetyNotes || ''}
              onChange={handleChange('safetyNotes')}
              margin="normal"
              variant="outlined"
              multiline
              rows={3}
              placeholder="e.g., Heat advisory in effect. Hydrate hourly. PPE required in Shop A."
              helperText={`${safetyLen} characters`}
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: '0.95rem',
                  fontWeight: 600
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.default, 0.3)
                    : alpha(theme.palette.grey[50], 0.5),
                  borderRadius: 1,
                  transition: 'all 0.2s ease',
                  '& fieldset': {
                    borderWidth: 1.5
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.default, 0.4)
                      : alpha(theme.palette.grey[50], 0.8),
                    '& fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  },
                  '&.Mui-focused': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.main, 0.05)
                      : alpha(theme.palette.primary.main, 0.02),
                    '& fieldset': {
                      borderWidth: 2
                    }
                  }
                }
              }}
            />
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>Quick add:</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                {commonSafety.map((txt, idx) => (
                  <Chip key={`safety_sugg_${idx}`} label={txt} size="small" variant="outlined" onClick={() => appendToField('safetyNotes', txt)} sx={{ cursor: 'pointer' }} />
                ))}
              </Box>
            </Box>
            </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>
        
        <Grid item xs={12}>
          <Accordion elevation={0} expanded={expandedPanels.weather} onChange={togglePanel('weather')} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" component="h3" sx={titleSx}>
                <WbSunnyIcon sx={{ mr: 1 }} /> Weather Notes
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
            <Box sx={sectionCard}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
              <Typography variant="subtitle1" component="h3" sx={titleSx}>
                <WbSunnyIcon sx={{ mr: 1 }} /> Weather Notes
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Get Weather">
                  <Button
                    variant="outlined"
                    startIcon={<WbSunnyIcon />}
                    onClick={() => setWeatherSearchOpen(true)}
                    size="small"
                  >
                    Get Weather
                  </Button>
                </Tooltip>
                <Tooltip title="Copy Weather Notes">
                  <IconButton size="small" onClick={handleCopyWeather}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Clear Weather Notes">
                  <IconButton size="small" onClick={handleClearWeather}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <TextField
              fullWidth
              label="Weather Information"
              value={data.weatherNotes || ''}
              onChange={handleChange('weatherNotes')}
              margin="normal"
              variant="outlined"
              multiline
              rows={4}
              placeholder="Auto-fill available. Include sunrise/sunset, temps, wind, precipitation."
              helperText={`${(data?.weatherNotes || '').length} characters${weatherCopied ? ' • Copied!' : ''}`}
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: '0.95rem',
                  fontWeight: 600
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.default, 0.3)
                    : alpha(theme.palette.grey[50], 0.5),
                  borderRadius: 1,
                  transition: 'all 0.2s ease',
                  '& fieldset': {
                    borderWidth: 1.5
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.default, 0.4)
                      : alpha(theme.palette.grey[50], 0.8),
                    '& fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  },
                  '&.Mui-focused': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.main, 0.05)
                      : alpha(theme.palette.primary.main, 0.02),
                    '& fieldset': {
                      borderWidth: 2
                    }
                  }
                }
              }}
            />
            </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Department Notes */}
        <Grid item xs={12}>
          <Typography 
            variant="h5"
            component="h3"
            sx={{
              mt: 2,
              mb: 1,
              fontWeight: 700,
              color: theme.palette.text.primary
            }}
          >
            Department Notes
          </Typography>
        </Grid>

        {/* Camera */}
        <Grid item xs={12} md={6}>
          <Accordion elevation={0} expanded={expandedPanels.camera} onChange={togglePanel('camera')} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={titleSx}>Camera</Typography>
            </AccordionSummary>
            <AccordionDetails>
            <Box sx={sectionCard}>
              {renderSuggestionChips('Camera', 'cameraNotes', suggestions?.camera)}
              <TextField
                fullWidth
                label="Camera Notes"
                value={data.cameraNotes || ''}
                onChange={handleChange('cameraNotes')}
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
                sx={textFieldSx}
              />
            </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Lighting */}
        <Grid item xs={12} md={6}>
          <Accordion elevation={0} expanded={expandedPanels.lighting} onChange={togglePanel('lighting')} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={titleSx}>Lighting</Typography>
            </AccordionSummary>
            <AccordionDetails>
            <Box sx={sectionCard}>
              {renderSuggestionChips('Lighting', 'lightingNotes', suggestions?.lighting)}
              <TextField
                fullWidth
                label="Lighting Notes"
                value={data.lightingNotes || ''}
                onChange={handleChange('lightingNotes')}
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
                sx={textFieldSx}
              />
            </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Props */}
        <Grid item xs={12} md={6}>
          <Accordion elevation={0} expanded={expandedPanels.props} onChange={togglePanel('props')} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={titleSx}>Props</Typography>
            </AccordionSummary>
            <AccordionDetails>
            <Box sx={sectionCard}>
              {renderSuggestionChips('Props', 'propsNotes', suggestions?.props)}
              <TextField
                fullWidth
                label="Props Notes"
                value={data.propsNotes || ''}
                onChange={handleChange('propsNotes')}
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
                sx={textFieldSx}
              />
            </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Sound */}
        <Grid item xs={12} md={6}>
          <Accordion elevation={0} expanded={expandedPanels.sound} onChange={togglePanel('sound')} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={titleSx}>Sound</Typography>
            </AccordionSummary>
            <AccordionDetails>
            <Box sx={sectionCard}>
              {renderSuggestionChips('Sound', 'soundNotes', suggestions?.sound)}
              <TextField
                fullWidth
                label="Sound Notes"
                value={data.soundNotes || ''}
                onChange={handleChange('soundNotes')}
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
                sx={textFieldSx}
              />
            </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* VFX */}
        <Grid item xs={12} md={6}>
          <Accordion elevation={0} expanded={expandedPanels.vfx} onChange={togglePanel('vfx')} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={titleSx}>VFX</Typography>
            </AccordionSummary>
            <AccordionDetails>
            <Box sx={sectionCard}>
              {renderSuggestionChips('VFX', 'vfxNotes', suggestions?.vfx)}
              <TextField
                fullWidth
                label="VFX Notes"
                value={data.vfxNotes || ''}
                onChange={handleChange('vfxNotes')}
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
                sx={textFieldSx}
              />
            </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Special FX */}
        <Grid item xs={12} md={6}>
          <Accordion elevation={0} expanded={expandedPanels.specialFx} onChange={togglePanel('specialFx')} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={titleSx}>Special FX</Typography>
            </AccordionSummary>
            <AccordionDetails>
            <Box sx={sectionCard}>
              {renderSuggestionChips('Special FX', 'specialFxNotes', suggestions?.specialFx)}
              <TextField
                fullWidth
                label="Special FX Notes"
                value={data.specialFxNotes || ''}
                onChange={handleChange('specialFxNotes')}
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
                sx={textFieldSx}
              />
            </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Vehicles */}
        <Grid item xs={12} md={6}>
          <Accordion elevation={0} expanded={expandedPanels.vehicles} onChange={togglePanel('vehicles')} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={titleSx}>Vehicles</Typography>
            </AccordionSummary>
            <AccordionDetails>
            <Box sx={sectionCard}>
              {renderSuggestionChips('Vehicles', 'vehiclesNotes', suggestions?.vehicles)}
              <TextField
                fullWidth
                label="Vehicles Notes"
                value={data.vehiclesNotes || ''}
                onChange={handleChange('vehiclesNotes')}
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
                sx={textFieldSx}
              />
            </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Stunts */}
        <Grid item xs={12} md={6}>
          <Accordion elevation={0} expanded={expandedPanels.stunts} onChange={togglePanel('stunts')} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={titleSx}>Stunts</Typography>
            </AccordionSummary>
            <AccordionDetails>
            <Box sx={sectionCard}>
              {renderSuggestionChips('Stunts', 'stuntsNotes', suggestions?.stunts)}
              <TextField
                fullWidth
                label="Stunts Notes"
                value={data.stuntsNotes || ''}
                onChange={handleChange('stuntsNotes')}
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
                sx={textFieldSx}
              />
            </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Animals */}
        <Grid item xs={12} md={6}>
          <Accordion elevation={0} expanded={expandedPanels.animals} onChange={togglePanel('animals')} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={titleSx}>Animals</Typography>
            </AccordionSummary>
            <AccordionDetails>
            <Box sx={sectionCard}>
              {renderSuggestionChips('Animals', 'animalsNotes', suggestions?.animals)}
              <TextField
                fullWidth
                label="Animals Notes"
                value={data.animalsNotes || ''}
                onChange={handleChange('animalsNotes')}
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
                sx={textFieldSx}
              />
            </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Hair/Makeup */}
        <Grid item xs={12} md={6}>
          <Accordion elevation={0} expanded={expandedPanels.hairMakeup} onChange={togglePanel('hairMakeup')} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={titleSx}>Hair/Makeup</Typography>
            </AccordionSummary>
            <AccordionDetails>
            <Box sx={sectionCard}>
              {renderSuggestionChips('Hair/Makeup', 'hairMakeupNotes', suggestions?.hairMakeup)}
              <TextField
                fullWidth
                label="Hair/Makeup Notes"
                value={data.hairMakeupNotes || ''}
                onChange={handleChange('hairMakeupNotes')}
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
                sx={textFieldSx}
              />
            </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Misc (Consolidated Catch-all) */}
        <Grid item xs={12}>
          <Accordion elevation={0} expanded={expandedPanels.misc} onChange={togglePanel('misc')} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={titleSx}>Misc</Typography>
            </AccordionSummary>
            <AccordionDetails>
            <Box sx={sectionCard}>
              {renderSuggestionChips('Misc', 'miscNotes', suggestions?.misc)}
              <TextField
                fullWidth
                label="Misc Notes"
                value={data.miscNotes || ''}
                onChange={handleChange('miscNotes')}
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
                placeholder="Additional notes that don't fit a department."
                helperText="Keep concise. Bulleted lines preferred."
              />
            </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
      
      {/* Weather Search Dialog */}
      <WeatherSearch
        open={weatherSearchOpen}
        onClose={() => setWeatherSearchOpen(false)}
        onSelectWeather={handleWeatherSelect}
        initialLocation={data.mainLocation || ''}
      />

      {/* Parking Map Dialog */}
      {mapOpen && activeParkingIndex !== null && (
        <LocationMap
          open={mapOpen}
          onClose={() => setMapOpen(false)}
          location={{
            name: parkingLocations[activeParkingIndex]?.name,
            address: parkingLocations[activeParkingIndex]?.address,
            placeData: parkingLocations[activeParkingIndex]?.placeData
          }}
        />
      )}
    </Paper>
  );
};

export default Notes;
